/**
 * El circuito de un requerimiento, completo, dentro de un panel.
 *
 * Es el equivalente de `PasosNotificacion` de Recuperos, con la diferencia que
 * obliga a escribirlo en vez de portarlo: allí un requerimiento es **una** carta
 * con nueve pasos fijos; aquí abre **N consultas en paralelo**, una por canal, y
 * cada canal tiene su propia longitud —9 el físico, 3 el correo y la mesa
 * virtual, 2 la consulta web—. El panel tiene que mostrar el abanico, no una
 * línea.
 *
 * **Los pasos no registrados se muestran en gris, no se ocultan.** La razón es
 * de Recuperos y vale igual aquí: que el notificador no haya informado todavía
 * *es* información, no ausencia de ella. Quien mira este panel quiere saber qué
 * falta, y una lista que solo enseña lo hecho no lo dice.
 *
 * `ARQ-8`: las descripciones de los pasos salen del catálogo servido, nunca
 * escritas aquí. `ARQ-4`: los tonos vienen con cada consulta.
 */
import { StatusBadge } from '@mpfn/ui'
import { canalDe, etiquetaDe, useCatalogos } from '../api/catalogos.js'
import type { Consulta, Requerimiento } from '../api/tipos.js'
import { diasHabiles, fecha } from '../lib/formato.js'

export function SeguimientoDeConsulta({ requerimiento }: { requerimiento: Requerimiento }) {
  const catalogos = useCatalogos()

  return (
    <div className="sigfisc-circuito">
      <header className="sigfisc-circuito__cabecera">
        <strong className="sigfisc-circuito__titulo">
          Requerimiento {requerimiento.ordinal}
        </strong>
        {requerimiento.anulada ? (
          <StatusBadge tone="neutral" label="Anulado" />
        ) : requerimiento.desenlace !== undefined ? (
          <StatusBadge
            tone="good"
            label={etiquetaDe(catalogos.desenlacesRequerimiento, requerimiento.desenlace)}
          />
        ) : (
          <StatusBadge tone="info" label="En verificación" />
        )}
        <span className="sigfisc-circuito__meta">
          Oficio {requerimiento.numeroOficio} · emitido el {fecha(requerimiento.fechaEmision)}
        </span>
      </header>

      {requerimiento.consultas.length === 0 ? (
        <p className="sigfisc-circuito__vacio">
          Este requerimiento no tiene consultas abiertas.
        </p>
      ) : (
        <ul className="sigfisc-circuito__consultas">
          {requerimiento.consultas.map((consulta) => (
            <li key={consulta.id}>
              <ConsultaDelCircuito
                consulta={consulta}
                // `NEG-8`: la que cerró el requerimiento se marca, porque es la
                // que explica por qué las hermanas quedaron abandonadas.
                esResolutoria={consulta.id === requerimiento.consultaResolutoriaId}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ConsultaDelCircuito({
  consulta,
  esResolutoria,
}: {
  consulta: Consulta
  esResolutoria: boolean
}) {
  const catalogos = useCatalogos()

  /**
   * Los pasos DECLARADOS del canal, cruzados con los registrados.
   *
   * El recorrido lo marca el catálogo —que es el contrato— y el registro solo
   * aporta fecha, responsable y observación. Recorrer `consulta.pasos` en
   * cambio habría enseñado únicamente lo ya hecho, que es el defecto que esta
   * pieza viene a corregir.
   */
  const declarados = canalDe(catalogos, consulta.canal)?.pasos ?? []
  const registrados = new Map(consulta.pasos.map((paso) => [paso.paso, paso]))

  return (
    <article
      className="sigfisc-circuito__consulta"
      data-resolutoria={esResolutoria ? 'si' : 'no'}
      data-abandonada={consulta.desenlace === 'abandonada' ? 'si' : 'no'}
      data-anulada={consulta.anulada ? 'si' : 'no'}
    >
      <header className="sigfisc-circuito__canal">
        <span className="sigfisc-circuito__canal-nombre">
          {consulta.canalEtiqueta}
          <span className="sigfisc-circuito__destino">{consulta.destinoTexto}</span>
        </span>
        <StatusBadge
          tone={consulta.anulada ? 'neutral' : consulta.tono}
          label={
            consulta.anulada
              ? 'Anulada'
              : consulta.desenlace !== undefined
                ? etiquetaDe(catalogos.desenlacesConsulta, consulta.desenlace)
                : `${String(consulta.paso)} de ${String(consulta.totalPasos)}`
          }
        />
      </header>

      {consulta.fechaVencimiento !== undefined && consulta.diasHabilesRestantes !== undefined && (
        // El plazo va FUERA de la línea de pasos, porque son dos ejes
        // distintos: una consulta en el paso 5 ya tiene el reloj corriendo y
        // una en el último puede seguir dentro de plazo.
        <p className="sigfisc-circuito__plazo" data-vencida={consulta.vencida ? 'si' : 'no'}>
          Vence el {fecha(consulta.fechaVencimiento)} · {diasHabiles(consulta.diasHabilesRestantes)}
        </p>
      )}

      {/*
       * Aquí NO va `WorkflowProgress` del design system, y conviene decir por
       * qué porque `SISTEMA_DISENO.md` §3.1 lo pedía.
       *
       * Se montó y se midió en este mismo panel, con estas tres consultas:
       *
       * | Canal        | Pasos | `WorkflowProgress` | Esta lista |
       * |--------------|-------|--------------------|------------|
       * | Consulta web |   2   |       107 px       |    52 px   |
       * | Correo       |   3   |       186 px       |    80 px   |
       * | Físico       |   9   |     **483 px**     |  **248 px**|
       *
       * Lo que descarta no es el alto, es la forma: su CSS es
       * `repeat(auto-fit, minmax(170px, 1fr))`, o sea una rejilla de tarjetas,
       * y en el ancho de este panel se resuelve en **dos columnas**. Los nueve
       * pasos del canal físico quedan leyéndose izquierda-derecha-abajo, que
       * en una secuencia con fechas es exactamente lo que no se puede hacer.
       * Además su `detail` es un `string`, así que la fecha y el responsable
       * pierden su columna y dejan de alinearse entre pasos.
       *
       * `WorkflowProgress` está pensado para pocas etapas anchas —un trámite
       * de tres o cuatro—, no para un rastro de auditoría de nueve con fecha.
       * La divergencia queda registrada; es lo que faltaba, no la divergencia.
       */}
      <ol className="sigfisc-circuito__pasos">
        {declarados.map((declarado) => {
          const registrado = registrados.get(declarado.paso)
          return (
            <li
              key={declarado.paso}
              className="sigfisc-circuito__paso"
              data-registrado={registrado ? 'si' : 'no'}
            >
              <span className="sigfisc-circuito__paso-numero">{declarado.paso}</span>
              <span className="sigfisc-circuito__paso-texto">
                {declarado.descripcion}
                {registrado?.observacion !== undefined && (
                  <span className="sigfisc-circuito__paso-observacion">
                    {registrado.observacion}
                  </span>
                )}
              </span>
              <span className="sigfisc-circuito__paso-fecha">
                {registrado ? fecha(registrado.fecha) : 'Sin registrar'}
              </span>
              <span className="sigfisc-circuito__paso-actor">
                {/*
                 * Quien lo ejecutó se muestra con su ETIQUETA, no con su
                 * código. El registro guarda `fiscalizacion` y `mesa_partes`,
                 * y volcarlos tal cual dejaba en pantalla un identificador en
                 * minúsculas con guion bajo, justo al lado de los pasos sin
                 * registrar, que sí traían «Mesa de Partes». `etiquetaDe`
                 * devuelve el código si no lo encuentra, así que un actor
                 * nuevo se vería raro pero nunca se perdería.
                 */}
                {registrado
                  ? etiquetaDe<string>(catalogos.actores, registrado.actorRol)
                  : declarado.actorEtiqueta}
              </span>
            </li>
          )
        })}
      </ol>

      {consulta.respuesta && (
        <p className="sigfisc-circuito__respuesta">
          <strong>
            {etiquetaDe(catalogos.pronunciamientos, consulta.respuesta.pronunciamiento)}
          </strong>
          {' · '}
          {fecha(consulta.respuesta.fechaRecepcion)}
        </p>
      )}
    </article>
  )
}
